import React, { useEffect, useRef, useState } from 'react';

interface AnimatedCounterProps {
  value: number;
  /**
   * Starting value on first mount. Defaults to 0.
   * Pass the previous balance so the counter starts there rather than at 0.
   */
  from?: number;
  /** Animation duration in ms. Defaults to 700. */
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Optional formatter applied to the displayed integer each frame. */
  formatFn?: (n: number) => string;
}

/**
 * Counts from `from` (default 0) to `value` on first mount, then from the
 * previous value to the new one on subsequent changes. Uses cubic ease-out so
 * the motion feels snappy at the start and settles smoothly at the end.
 */
export function AnimatedCounter({
  value,
  from = 0,
  duration = 700,
  className,
  style,
  formatFn,
}: AnimatedCounterProps) {
  const [displayed, setDisplayed] = useState(from);
  const prevValueRef = useRef(from);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = prevValueRef.current;
    const to = value;

    if (from === to) {
      setDisplayed(to);
      return;
    }

    const startTime = performance.now();
    const diff = to - from;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Cubic ease-out: fast start, smooth finish
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(from + diff * eased));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevValueRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  return (
    <span className={className} style={style}>
      {formatFn ? formatFn(displayed) : displayed.toString()}
    </span>
  );
}
